// @ts-nocheck
/*
  <pb-eoy-giftbag-atc> — EOY 2026 add to cart + giftbag pop-up
  (blocks/pb-eoy-giftbag-atc.liquid). Docs: .cursor/references/eoy-giftbag-popup.md

  - The ATC button (snippets/add-to-cart-button) sits in the buy buttons'
    /cart/add form (this is a static block of blocks/buy-buttons.liquid).
    Its click is taken over here (preventDefault on the click, so the form never
    submits and PBCart's auto-hook doesn't add a second line); other click
    listeners (Converge data-cvg-atc, Dalton, Horizon's "added" animation)
    still see the click.
  - Order (ticket): the box is added and the cart drawer opens as usual
    (PBCart.addLine), THEN the pop-up opens on top of the drawer, once the
    drawer has slid in (POPUP_DELAY_MS). PBOverlayStack puts the pop-up above
    the drawer because it is pushed later.
  - "In winkelwagen +€x": adds the selected giftbag (the open drawer updates
    itself; its open is a no-op when already open) and closes the pop-up.
    Skip / close / Esc / backdrop: closes the pop-up; the drawer stays open.
  - Esc is stopped at the pop-up: the drawer closes on any Escape on document.
  - Pop-up = role="dialog" aria-modal, focus trapped; on close focus goes back
    to what had it before (the drawer), else the ATC button. Scroll lock on
    <html> only, like the drawer (on <body> it breaks position: sticky).
*/
(() => {
  if (customElements.get('pb-eoy-giftbag-atc')) return;

  const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';
  // The drawer slides in over 350ms (assets/unified-cart-drawer.css); the pop-up comes after.
  const POPUP_DELAY_MS = 450;

  async function waitForPBCart(tries = 80, delay = 50) {
    for (let i = 0; i < tries; i++) {
      if (window.PBCart && typeof window.PBCart.addLine === 'function') return window.PBCart;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    throw new Error('PBCart.addLine is niet beschikbaar');
  }

  // addLine opens (or refreshes) the drawer itself.
  async function addLine(variantId) {
    const cart = await waitForPBCart();
    return cart.addLine(variantId, 1);
  }

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  class PbEoyGiftbagAtc extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-gba-dialog]');
      this.radios = this.dialog ? Array.from(this.dialog.querySelectorAll('.pb-eoy-gba__radio')) : [];
      this.hasPopup = this.radios.some((radio) => !radio.disabled);

      this.onClick = this.onClick.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogChange = this.onDialogChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.addEventListener('click', this.onClick);
      // The dialog is portaled to <body> while open, so it gets its own listeners.
      this.dialog?.addEventListener('click', this.onDialogClick);
      this.dialog?.addEventListener('change', this.onDialogChange);
      this.dialog?.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      // A dialog left open (theme editor reload) must not keep the page locked.
      if (this.isOpen) this.close({ restoreFocus: false });
    }

    get isOpen() {
      return this.dialog && !this.dialog.hidden;
    }

    onClick(event) {
      const button = event.target.closest('[data-atc-button]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      this.addBox(button);
    }

    async addBox(button) {
      if (button.dataset.pending === 'true') return;
      // The buy buttons' product form (scoped: never a global /cart/add form).
      const variantId = this.closest('form')?.querySelector('[name="id"]')?.value || this.dataset.variantId;
      if (!variantId) return;

      // Not `disabled`: that would drop keyboard focus from the button.
      button.dataset.pending = 'true';
      button.setAttribute('aria-busy', 'true');
      this.atcButton = button;

      try {
        await addLine(variantId); // the drawer opens
        if (this.hasPopup) {
          await wait(POPUP_DELAY_MS);
          this.open();
        }
      } catch (error) {
        console.error('[EOY giftbag] adding the box failed', error);
      } finally {
        delete button.dataset.pending;
        button.removeAttribute('aria-busy');
      }
    }

    // ---------- Pop-up ----------

    open() {
      if (this.isOpen) return;
      this.selectInitial();
      this.showError(false);
      this.returnFocus = document.activeElement;

      this.dialog.hidden = false;
      window.PBOverlayStack?.push(this.dialog);

      this.scrollLock = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';

      const checked = this.radios.find((radio) => radio.checked);
      (checked || this.dialog.querySelector(FOCUSABLE))?.focus({ preventScroll: true });
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      this.dialog.hidden = true;
      window.PBOverlayStack?.pop(this.dialog);

      if (this.scrollLock !== undefined) {
        document.documentElement.style.overflow = this.scrollLock;
        this.scrollLock = undefined;
      }

      if (!restoreFocus) return;
      const target =
        this.returnFocus?.isConnected && this.returnFocus !== document.body ? this.returnFocus : this.atcButton;
      target?.focus({ preventScroll: true });
    }

    // Preselected giftbag (block setting) if available, else the first available one.
    selectInitial() {
      const enabled = this.radios.filter((radio) => !radio.disabled);
      const preselect = this.dataset.preselect;
      const initial = (preselect && enabled.find((radio) => radio.dataset.handle === preselect)) || enabled[0];
      if (initial) initial.checked = true;
      this.updatePrice();
    }

    updatePrice() {
      const checked = this.radios.find((radio) => radio.checked);
      const price = this.dialog.querySelector('[data-gba-price]');
      if (price) price.textContent = checked?.dataset.price ? `+${checked.dataset.price}` : '';
      const add = this.dialog.querySelector('[data-gba-add]');
      if (add) add.setAttribute('aria-disabled', String(!checked));
    }

    showError(show) {
      const error = this.dialog.querySelector('[data-gba-error]');
      if (error) error.hidden = !show;
    }

    onDialogChange(event) {
      if (event.target.matches('.pb-eoy-gba__radio')) {
        this.showError(false);
        this.updatePrice();
      }
    }

    onDialogClick(event) {
      if (event.target.closest('[data-gba-dismiss]')) {
        this.close();
        return;
      }
      const add = event.target.closest('[data-gba-add]');
      if (add) this.addGiftbag(add);
    }

    async addGiftbag(button) {
      const checked = this.radios.find((radio) => radio.checked);
      if (!checked || button.dataset.pending === 'true') return;

      button.dataset.pending = 'true';
      button.setAttribute('aria-busy', 'true');
      this.showError(false);

      try {
        await addLine(checked.value);
        this.close();
      } catch (error) {
        console.error('[EOY giftbag] adding the giftbag failed', error);
        this.showError(true);
      } finally {
        delete button.dataset.pending;
        button.removeAttribute('aria-busy');
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        // Only the pop-up: the drawer underneath closes on any Escape that reaches document.
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      // Focus trap. Radios: only the checked one is in the tab order.
      const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null && (el.type !== 'radio' || el.checked),
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('pb-eoy-giftbag-atc', PbEoyGiftbagAtc);
})();
